import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

import { COLORS, PROP_DEFINITIONS } from "./dioramaLayout";
import { InteractiveProp } from "./InteractiveProp";

const DEF = PROP_DEFINITIONS.find((p) => p.id === "mailbox")!;

function createRoofGeometry(): THREE.BufferGeometry {
  const verts = new Float32Array([
    // Bottom face
    -0.5, 0, -0.35, 0.5, 0, -0.35, 0.5, 0, 0.35,
    -0.5, 0, -0.35, 0.5, 0, 0.35, -0.5, 0, 0.35,
    // Left slope
    -0.5, 0, -0.35, -0.5, 0, 0.35, 0, 0.25, 0,
    // Right slope
    0.5, 0, -0.35, 0.5, 0, 0.35, 0, 0.25, 0,
    // Front triangle
    -0.5, 0, -0.35, 0, 0.25, 0, 0.5, 0, -0.35,
    // Back triangle
    -0.5, 0, 0.35, 0, 0.25, 0, 0.5, 0, 0.35,
  ]);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  geo.computeVertexNormals();
  return geo;
}

/** Visual-only mailbox — reusable outside the engine's InteractiveProp wrapper. */
export function MailboxModel() {
  const flagPoleRef = useRef<THREE.Mesh>(null);
  const flagTipRef = useRef<THREE.Mesh>(null);
  const roofGeo = useMemo(createRoofGeometry, []);

  useFrame(({ clock }) => {
    const sway = Math.sin(clock.elapsedTime * 1.2) * 0.1;
    if (flagPoleRef.current) flagPoleRef.current.rotation.z = sway;
    if (flagTipRef.current) flagTipRef.current.rotation.z = sway;
  });

  return (
    <group>
      {/* Post */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <boxGeometry args={[0.2, 1.2, 0.2]} />
        <meshStandardMaterial color={COLORS.sandDark} flatShading />
      </mesh>

      {/* Box body */}
      <mesh position={[0, 1.55, 0]} castShadow>
        <boxGeometry args={[0.9, 0.7, 0.6]} />
        <meshStandardMaterial color={COLORS.purple} flatShading />
      </mesh>

      {/* Roof */}
      <mesh geometry={roofGeo} position={[0, 1.9, 0]} castShadow>
        <meshStandardMaterial color={COLORS.purpleDeep} flatShading />
      </mesh>

      {/* Mail slot */}
      <mesh position={[0, 1.6, 0.32]}>
        <boxGeometry args={[0.5, 0.06, 0.02]} />
        <meshStandardMaterial color="#333333" flatShading />
      </mesh>

      {/* Flag pole */}
      <mesh ref={flagPoleRef} position={[0.5, 1.6, 0]}>
        <boxGeometry args={[0.05, 0.35, 0.02]} />
        <meshStandardMaterial color={COLORS.coral1} flatShading />
      </mesh>

      {/* Flag tip */}
      <mesh ref={flagTipRef} position={[0.6, 1.75, 0]}>
        <boxGeometry args={[0.2, 0.12, 0.02]} />
        <meshStandardMaterial color={COLORS.coral1} flatShading />
      </mesh>

      {/* Peeking letter */}
      <mesh position={[0.05, 1.68, 0.33]} rotation={[0.15, 0, 0]}>
        <boxGeometry args={[0.35, 0.25, 0.02]} />
        <meshStandardMaterial color={COLORS.cream} flatShading />
      </mesh>
    </group>
  );
}

export function OfficeMailbox() {
  return (
    <InteractiveProp
      propId={DEF.id}
      label={DEF.label}
      clickMessage={DEF.clickMessage}
      position={DEF.position}
    >
      <MailboxModel />
    </InteractiveProp>
  );
}
